'use client';
// lib/hooks/useEventTracking.ts
// Site-wide automatic interaction tracking via a single delegated click
// listener — no per-component wiring needed. Recognises:
//   • [data-track]            → custom event (data-track-category / data-track-target)
//   • <a href="mailto:">      → copy_email   (conversion)
//   • <a href="tel:">         → copy_phone   (conversion)
//   • <a download> / *.pdf…   → cv_download / file_download (conversion)
//   • outbound links          → social_click / outbound_link
import { useEffect } from 'react';
import { trackEvent, type EventCategory } from '@/lib/utils/tracking';

const FILE_RE   = /\.(pdf|docx?|pptx?|xlsx?|zip|rar|csv)(\?|#|$)/i;
const CV_RE     = /\b(cv|resume|curriculum)\b/i;
const SOCIAL_RE = /(github|linkedin|twitter|x\.com|facebook|instagram|youtube|dribbble|behance|medium|dev\.to|telegram|wa\.me|whatsapp)/i;

export function useEventTracking() {
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            const start = e.target as HTMLElement | null;
            if (!start || typeof start.closest !== 'function') return;

            // 1) Explicit opt-in via data attributes (highest priority)
            const tagged = start.closest<HTMLElement>('[data-track]');
            if (tagged) {
                trackEvent({
                    type:     tagged.getAttribute('data-track') || 'click',
                    category: (tagged.getAttribute('data-track-category') as EventCategory) || undefined,
                    target:   tagged.getAttribute('data-track-target') || tagged.textContent?.trim().slice(0, 80) || undefined,
                });
                return;
            }

            // 2) Anchor-based heuristics
            const anchor = start.closest<HTMLAnchorElement>('a[href]');
            if (!anchor) return;
            const href = anchor.getAttribute('href') || '';
            if (!href || href.startsWith('#')) return;

            if (href.startsWith('mailto:')) {
                trackEvent({ type: 'copy_email', category: 'conversion', target: href.slice(7) });
                return;
            }
            if (href.startsWith('tel:')) {
                trackEvent({ type: 'copy_phone', category: 'conversion', target: href.slice(4) });
                return;
            }
            if (anchor.hasAttribute('download') || FILE_RE.test(href)) {
                const isCv = CV_RE.test(href) || CV_RE.test(anchor.textContent || '');
                trackEvent({ type: isCv ? 'cv_download' : 'file_download', category: 'conversion', target: href });
                return;
            }

            // Outbound vs internal
            try {
                const url = new URL(href, window.location.origin);
                if (url.host && url.host !== window.location.host) {
                    const social = SOCIAL_RE.test(url.host);
                    trackEvent({
                        type:     social ? 'social_click' : 'outbound_link',
                        category: social ? 'conversion' : 'navigation',
                        target:   url.href,
                    });
                }
            } catch {
                // not a parseable URL — ignore
            }
        };

        // Capture phase so we still see clicks that call stopPropagation()
        document.addEventListener('click', onClick, { capture: true });
        return () => document.removeEventListener('click', onClick, { capture: true } as EventListenerOptions);
    }, []);
}
